import React, { useEffect, useMemo } from 'react';
import { Button } from '@amzn/stencil-react-components/button';
import { useNavigate } from 'react-router-dom';
import { Text, H1, H4, Label } from '@amzn/stencil-react-components/text';
import { useTranslation } from 'react-i18next';
import { Flex, Spacer, Col, Hr } from '@amzn/stencil-react-components/layout';
import { Card } from '@amzn/stencil-react-components/card';
import { Stat, StatType } from '@amzn/stencil-react-components/data-visualization';
import { useGlobalContext, useUserDataContext } from '../../hooks';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import partyPopper from '../../images/party-popper.png';
import { FeedbackParam } from '../../types/backendService';
import { FEEDBACK_MONTHS_LOOKBACK } from '../../constants/feedbackConfiguration';
import { titleizeString } from '../../util/string_utils';
import { colorForFeedback } from '../../util/feedback_styling';
import { generateFeedbackDocumentFields } from '../../util/feedback_document_utils';
import { metricSender } from '../../util/metric_sender';

const feedbackCategories = ['Positive'];

export const PositiveFeedbackSummary = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { areMetricsSendable, feedbacks: feedbacksContext, spprSummaries, sqprSummaries } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const feedbacks: FeedbackParam[]|undefined = (feedbacksContext || [])
      .filter((feedback: FeedbackParam) => feedbackCategories.includes(feedback.feedbackCategory!));
  const loading = (feedbacksContext == undefined);

  useEffect(() => {
    if (areMetricsSendable) {
      metricSender.generateCompetenciesMetrics(
        userDetails,
        spprSummaries,
        sqprSummaries,
        'PositiveFeedbacks',
      );
    }
  }, [areMetricsSendable]);

  const feedbackContent = useMemo(() => {
    return feedbacks.sort((a, b) => b.deliveredDateUtc! - a.deliveredDateUtc!)
        .map((feedback: FeedbackParam, index: number) => {
      let feedbackText = [];

      let feedbackDocumentFields = generateFeedbackDocumentFields(feedback, t)!;

      if (feedbackDocumentFields.summary != undefined && feedbackDocumentFields.summary.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('summary')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.summary}
              </Text>
            </Col>
          </>
        );
      }
      if (feedbackDocumentFields.incidentDetails != undefined && feedbackDocumentFields.incidentDetails.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('incidentDetails')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.incidentDetails}
              </Text>
            </Col>
          </>
        );
      }
      if (feedbackDocumentFields.areasOfImprovement != undefined && feedbackDocumentFields.areasOfImprovement.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('areasOfImprovement')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.areasOfImprovement}
              </Text>
            </Col>
          </>
        );
      }

      return (
        <Card data-cy="feedback-card" key={index}>
          <Col width="100%">
            <Flex flexDirection="row" justifyContent="space-between" width="100%">
              <H4 color={colorForFeedback(feedback)}>{feedback.warehouseId?.toUpperCase()} - {titleizeString(feedback.type!)}</H4>
            </Flex>
            <Text fontWeight="light">
              <em>
                {t('deliveredByText', {
                  user: `${feedback.deliveredBy}@`,
                  date: new Date(feedback.deliveredDateUtc! * 1000).toLocaleDateString(),
                  interpolation: {'escapeValue': false},
                })}
              </em>
            </Text>
            {feedbackText}
          </Col>
        </Card>
      );
    })
  }, [feedbacks]);

  return(
    <>
      <Flex flexDirection="row" justifyContent="space-between">
        <H1>{t('positiveFeedbacks')}</H1>
        <Button data-cy="home-btn" onClick={()=>{navigate('/')}}>{t('home')}</Button>
      </Flex>

      <Spacer height={32}/>
      {loading &&
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <Spinner showText loadingText={t('loadingFeedbacks')} />
        </Flex>
      }
      {!loading &&
        <>
          <Flex flexDirection="row" justifyContent="space-between" width="100%" alignItems="center">
            <Card backgroundColor="green50" width="100%" justifyContent="center" alignItems="center">
              <Stat
                dataTestId="feedback-stat"
                type={StatType.Large}
                numeralText={feedbacks.length.toString()}
                isInverted={true}
                descriptionText={t('feedbacksDataTitle', { months: FEEDBACK_MONTHS_LOOKBACK })}
              />
            </Card>
            <img style={{ marginLeft: 32 }} src={partyPopper} width="100"/>
          </Flex>
          {feedbacks.length > 0 &&
            <>
              <Spacer height={16}/>

              <Text fontWeight="light">
                {`${t('positiveFeedbacksSubtitle')} ${t('yesterdaysDataTooltip')}`}
              </Text>

              <Spacer height={16}/>

              <Col gridGap="S300" >
                {feedbackContent}
              </Col>
            </>
          }
          {feedbacks.length == 0 &&
            <Text fontWeight="light">{`${t('feedbackTooltip')} ${t('yesterdaysDataTooltip')}`}</Text>
          }
        </>
      }
    </>
  );
}
